import React, { useEffect, useState } from 'react';
import api from '../../api/axios';
import { X, Clock, ArrowDownRight, ArrowUpRight } from 'lucide-react';

const formatWhen = (iso) => {
    try {
        const d = new Date(iso);
        return d.toLocaleString();
    } catch {
        return iso;
    }
};

const actionLabels = {
    recruiter_search: 'Recruiter search',
    hiring_signals_scan: 'Hiring signals scan',
    job_search_results: 'New jobs added',
    referral_score_unlock: 'Referral score unlocked',
    credits_purchase: 'Credits purchased',
    credit_event: 'Credit activity'
};

const actionDescriptions = {
    recruiter_search: (meta) => {
        const company = meta?.companyName ? ` for ${meta.companyName}` : '';
        const leads = meta?.leadCount ? ` • ${meta.leadCount} contact${Number(meta.leadCount) === 1 ? '' : 's'} checked` : '';
        return `Recruiter/contact lookup${company}${leads}`;
    },
    hiring_signals_scan: (meta) => {
        const count = meta?.strongSignalsAdded || meta?.signalsAdded;
        return count ? `${count} strong hiring signal${Number(count) === 1 ? '' : 's'} found` : 'Hiring signal discovery';
    },
    job_search_results: (meta) => {
        const count = meta?.jobsAdded;
        return count ? `${count} new job${Number(count) === 1 ? '' : 's'} added to your jobs list` : 'Job discovery results';
    },
    referral_score_unlock: (meta) => meta?.companyName ? `Referral score for ${meta.companyName}` : 'Referral score check',
    credits_purchase: (meta) => meta?.creditsAdded ? `${meta.creditsAdded} credits added to your account` : 'Credit pack purchase'
};

const getActionLabel = (action) => actionLabels[action] || String(action || 'credit_event').replace(/_/g, ' ');

const getActionDescription = (item) => {
    const fn = actionDescriptions[item?.action];
    if (fn) return fn(item?.meta || {});
    return item?.meta?.companyName || item?.source || '';
};

const CreditsUsageModal = ({ isOpen, onClose }) => {
    const [items, setItems] = useState([]);
    const [nextCursor, setNextCursor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = async (cursor = null) => {
        setLoading(true);
        setError(null);
        try {
            const res = await api.get('/api/credits/history', {
                params: { limit: 50, ...(cursor ? { cursor } : {}) },
                headers: { 'auth-token': localStorage.getItem('token') }
            });
            const payload = res.data;
            const newItems = Array.isArray(payload?.items) ? payload.items : [];
            setItems((prev) => (cursor ? [...prev, ...newItems] : newItems));
            setNextCursor(payload?.nextCursor || null);
        } catch (e) {
            setError(e?.response?.data?.message || e?.message || 'Failed to load credits history');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isOpen) return;
        load(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

            <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900 font-dmsans">Credits & Usage</h2>
                        <p className="text-xs text-gray-500 font-dmsans mt-0.5">See how many credits you’ve used and why.</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center"
                        aria-label="Close"
                    >
                        <X size={18} className="text-gray-700" />
                    </button>
                </div>

                <div className="p-4">
                    {error ? (
                        <div className="p-3 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm font-dmsans">
                            {error}
                        </div>
                    ) : null}

                    <div className="mt-3 space-y-2 max-h-[60vh] overflow-auto pr-1">
                        {items.length === 0 && !loading ? (
                            <div className="text-center py-10">
                                <div className="w-12 h-12 mx-auto rounded-full bg-gray-100 flex items-center justify-center">
                                    <Clock className="w-5 h-5 text-gray-500" />
                                </div>
                                <p className="mt-3 text-sm font-medium text-gray-700 font-dmsans">No usage history yet</p>
                                <p className="text-xs text-gray-500 font-dmsans mt-1">When you use credits, it will show up here.</p>
                            </div>
                        ) : (
                            items.map((it) => {
                                const amount = Number(it.amount || 0);
                                const isDebit = amount < 0;
                                const Icon = isDebit ? ArrowDownRight : ArrowUpRight;
                                const description = getActionDescription(it);
                                return (
                                    <div key={it._id} className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 bg-white">
                                        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${isDebit ? 'bg-rose-50' : 'bg-emerald-50'}`}>
                                            <Icon className={`${isDebit ? 'text-rose-600' : 'text-emerald-600'}`} size={18} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between gap-2">
                                                <p className="text-sm font-semibold text-gray-900 font-dmsans truncate">
                                                    {getActionLabel(it.action)}
                                                </p>
                                                <p className={`text-sm font-bold font-dmsans ${isDebit ? 'text-rose-700' : 'text-emerald-700'}`}>
                                                    {isDebit ? '' : '+'}{amount}
                                                </p>
                                            </div>
                                            {description ? (
                                                <p className="text-xs text-gray-600 font-dmsans mt-0.5 truncate">
                                                    {description}
                                                </p>
                                            ) : null}
                                            <p className="text-xs text-gray-500 font-dmsans mt-0.5">
                                                {formatWhen(it.createdAt)}{it.source ? ` • ${it.source}` : ''}{it.balanceAfter !== null && it.balanceAfter !== undefined ? ` • balance: ${it.balanceAfter}` : ''}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    <div className="mt-4 flex items-center justify-between">
                        <button
                            onClick={() => load(null)}
                            disabled={loading}
                            className="text-sm font-medium font-dmsans px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50"
                        >
                            Refresh
                        </button>
                        <button
                            onClick={() => nextCursor && load(nextCursor)}
                            disabled={loading || !nextCursor}
                            className="text-sm font-medium font-dmsans px-3 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50"
                        >
                            {nextCursor ? 'Load more' : 'All activity loaded'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreditsUsageModal;

